/**
 * Error Utilities
 * Centralized error parsing and formatting for the application
 */

/**
 * Parse server error and extract meaningful information
 * @param {Error} error - The error object from the API call
 * @returns {string} A user-friendly error message
 */
export const parseServerError = (error) => {
    try {
        // If error has a response property (axios error)
        if (error.response) {
            const { status, data, config } = error.response;
            
            // For 409 Conflict errors, prioritize the 'reason' field if available
            if (status === 409 && data?.reason) {
                return data.reason;
            }
            
            // Extract meaningful information from the response
            let errorCode = data?.error || 'Server Error';
            let reason = data?.reason || data?.message || 'An unexpected error occurred';
            
            // Build a clean error message
            let message = `Error: ${errorCode}`;
            
            // Add HTTP status if it's not 500 (500 is implied by "Server Error")
            if (status && status !== 500) {
                message = `HTTP ${status}: ${errorCode}`;
            }
            
            // Add the reason/message
            if (reason) {
                message += `\n${reason}`;
            }
            
            return message;
        }
        
        // If error has a message property
        if (error.message) {
            return error.message;
        }
        
        // If error is a string
        if (typeof error === 'string') {
            return error;
        }
        
        // Fallback
        return 'An unexpected error occurred';
    } catch (parseError) {
        console.error('Error parsing server error:', parseError);
        return error?.message || 'An unexpected error occurred';
    }
};

/**
 * Parse validation errors from server response
 * @param {Error} error - The error object from the API call
 * @returns {Object} An object with field names as keys and error messages as values
 */
export const parseValidationErrors = (error) => {
    try {
        if (error.response?.data?.errors) {
            // If server returns errors in a structured format
            return error.response.data.errors;
        }
        
        if (error.response?.data?.message) {
            // Try to extract field-specific errors from message
            const message = error.response.data.message;
            const fieldErrors = {};
            
            // Common patterns: "Field 'fieldName': error message"
            const fieldPattern = /(?:Field\s+['"](\w+)['"]|(\w+)\s+field):\s*([^,\n]+)/gi;
            let match;
            
            while ((match = fieldPattern.exec(message)) !== null) {
                const fieldName = match[1] || match[2];
                const errorMessage = match[3].trim();
                fieldErrors[fieldName] = errorMessage;
            }
            
            if (Object.keys(fieldErrors).length > 0) {
                return fieldErrors;
            }
        }
        
        return {};
    } catch (parseError) {
        console.error('Error parsing validation errors:', parseError);
        return {};
    }
};

/**
 * Check if error is a network error
 * @param {Error} error - The error object
 * @returns {boolean} True if it's a network error
 */
export const isNetworkError = (error) => {
    return !error.response && (
        error.message?.includes('Network Error') ||
        error.message?.includes('Failed to fetch') ||
        error.code === 'ECONNABORTED' ||
        error.code === 'ERR_NETWORK'
    );
};

/**
 * Check if error is an authentication error
 * @param {Error} error - The error object
 * @returns {boolean} True if it's an auth error (401, 403)
 */
export const isAuthError = (error) => {
    return error.response?.status === 401 || error.response?.status === 403;
};

/**
 * Check if error is a validation error
 * @param {Error} error - The error object
 * @returns {boolean} True if it's a validation error (400, 422)
 */
export const isValidationError = (error) => {
    return error.response?.status === 400 || error.response?.status === 422;
};

/**
 * Check if error is a conflict error
 * @param {Error} error - The error object
 * @returns {boolean} True if it's a conflict error (409)
 */
export const isConflictError = (error) => {
    return error.response?.status === 409;
};

/**
 * Get a user-friendly error title based on error type
 * @param {Error} error - The error object
 * @returns {string} A title for the error
 */
export const getErrorTitle = (error) => {
    if (isNetworkError(error)) {
        return 'Network Error';
    }
    if (isAuthError(error)) {
        return 'Authentication Error';
    }
    if (isValidationError(error)) {
        return 'Validation Error';
    }
    if (isConflictError(error)) {
        return 'Conflict Error';
    }
    if (error.response?.status >= 500) {
        return 'Server Error';
    }
    return 'Error';
};

/**
 * Format error for logging (includes full details)
 * @param {Error} error - The error object
 * @returns {Object} Structured error information for logging
 */
export const formatErrorForLogging = (error) => {
    return {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        endpoint: error.response?.config?.url,
        method: error.response?.config?.method?.toUpperCase(),
        data: error.response?.data,
        stack: error.stack
    };
};
